import React from 'react';

import { usePartnerLinkingImage } from 'microsites/partner-linking/use-partner-linking-images';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton from 'components/action-button';
import TimHortonsLogo from 'components/icons/tim-hortons-logo';

export interface ILinkingErrorProps {
  /** User-facing body copy. Callers pass an already-localized string. */
  message: string;
  /**
   * Heading and document title, already localized. Defaults to the generic
   * "Something went wrong" used by the `/partner-linking/error` route.
   */
  title?: string;
  /** "Back to Tim Hortons" CTA handler (source-aware: app deep link vs web home). */
  onBack: () => void;
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${Styles.color.background};
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Styles.color.primary};
  // Match the main app nav bar (height + red) so the microsite header is
  // consistent with the rest of the site.
  height: ${Styles.layout.navHeight.mobile};
  padding: 0 1rem;

  ${Styles.desktop`
    height: ${Styles.layout.navHeight.desktop};
  `}

  svg {
    height: 1.5rem;
    width: auto;

    // TH brand logo is red by default; invert to white over the red banner.
    path {
      fill: ${Styles.color.white};
    }
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  // Both breakpoints follow the design: content is top-aligned below the
  // header (never vertically centered). Mobile sits 2rem below the header;
  // desktop uses the larger ~6.5rem gap above the illustration (Figma
  // Partnerships node 1377:61836).
  justify-content: flex-start;
  text-align: center;
  padding: 2rem 1rem;
  margin: 0 auto;
  width: 100%;
  max-width: 24rem;

  ${Styles.desktop`
    max-width: ${Styles.layout.smallerSectionMaxWidth};
    padding: 6.5rem 1rem 3rem;
  `}
`;

// Illustration comes from the featurePartnerLinking singleton in Sanity.
const IllustrationContainer = styled.div`
  margin-bottom: 1.5rem;

  img {
    width: 12.5rem;
    height: 12.5rem;
  }
`;

const Title = styled.h1`
  color: ${Styles.color.black};
  font-family: ${Styles.fontFamily.base};
  font-size: 1.5rem;
  line-height: 2rem;
  margin: 0.5rem 0;

  ${Styles.desktop`
    font-size: 2rem;
    line-height: 2.5rem;
  `}
`;

const Description = styled.p`
  // Figma Tokens/secondary-1 — the design system token isn't wired into Styles.
  color: #654d4d;
  font-size: 1rem;
  line-height: 1.5rem;
  margin: 0 0 2rem 0;
`;

const CtaWrapper = styled.div`
  // Hidden on mobile: users reach this screen from the app and can return there
  // natively, so the design omits the CTA. Shown on desktop web only.
  display: none;
  width: 100%;
  max-width: 20rem;

  ${Styles.desktop`
    display: block;
  `}
`;

/**
 * Full-page error view shared by the partnership-linking flows, reached via
 * the partner-agnostic `/partner-linking/error` route. Matches the design's
 * header + illustration + title + body + primary CTA layout (mirrors the
 * already-linked / rate-limited screens).
 *
 * `message` is the localized, per-error body, and `title` optionally overrides
 * the generic heading for a caller that owns its own copy (THLE-4387's Triangle
 * account error). CTA copy is localized here. No raw/technical detail is
 * rendered — the caller logs that separately (see `error.tsx`) so users never
 * see untranslated internals.
 */
export default function LinkingError({ message, title, onBack }: ILinkingErrorProps): JSX.Element {
  const { formatMessage } = useIntl();
  const { imageUrl: errorImageUrl } = usePartnerLinkingImage('errorIllustration');
  const heading = title ?? formatMessage({ id: 'partnerLinkingErrorTitle' });

  return (
    <Page data-testid="partner-linking-error" role="alert">
      <Helmet title={heading} />
      <Header>
        <TimHortonsLogo />
      </Header>
      <Content>
        <IllustrationContainer aria-hidden>
          {errorImageUrl && <img src={errorImageUrl} alt="" />}
        </IllustrationContainer>
        <Title>{heading}</Title>
        <Description>{message}</Description>
        <CtaWrapper>
          <ActionButton fullWidth onClick={onBack} data-testid="partner-linking-error-back">
            {formatMessage({ id: 'partnerLinkingErrorBackCta' })}
          </ActionButton>
        </CtaWrapper>
      </Content>
    </Page>
  );
}
